import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const OfflineContext = createContext({
  isOnline: true,
  isOfflineReady: false,
  cacheTreeData: () => {},
});

export const useOffline = () => useContext(OfflineContext);

export const OfflineProvider = ({ children }) => {
  const isProduction = process.env.NODE_ENV === 'production';
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOfflineReady, setIsOfflineReady] = useState(false);

  useEffect(() => {
    // Register service worker only in production to avoid stale dev bundles/pages.
    if (isProduction && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
          setIsOfflineReady(true);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                console.log('New version available');
              }
            });
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }

    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      console.log('App is online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('App is offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isProduction]);

  // Helper to send tree data to service worker for caching
  const cacheTreeData = useCallback((treeId, data) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_TREE_DATA',
        treeId,
        data
      });
    }
  }, []);

  return (
    <OfflineContext.Provider value={{ isOnline, isOfflineReady, cacheTreeData }}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
